/**
 * Entry point do SPA admin.
 *
 * Boot:
 *  1. Inicializa MSAL (processa redirect de login se houver)
 *  2. Injeta CSS do layout
 *  3. Registra event delegation pros botões login/logout
 *  4. Inicia o router (despacha pra view correspondente à URL atual)
 */

import { api, ApiError } from '../lib/api.js';
import { entrar, sair } from '../lib/auth.js';
import { initMsal } from '../lib/msal.js';

import { injectLayoutStyles } from './layout.js';
import { initRouter } from './router.js';
import { renderDashboard } from './views/dashboard.js';
import { renderPrecosAdmin } from './views/precos-admin.js';
import { renderDetalheProposta } from './views/proposta-detalhe.js';
import { renderNovaPropostaGenerica, getTipoFromUrl } from './views/proposta-nova-generica.js';
import { renderSeletorTipoProposta } from './views/proposta-nova-tipo.js';
import { renderNovaProposta } from './views/proposta-nova.js';
import { renderListaPropostas } from './views/propostas-lista.js';

document.addEventListener('click', (event) => {
  const target = event.target as HTMLElement | null;
  const btn = target?.closest<HTMLButtonElement>('[data-action]');
  const action = btn?.dataset.action;
  if (action === 'login') {
    event.preventDefault();
    void entrar();
  } else if (action === 'logout') {
    event.preventDefault();
    void sair();
  } else if (action === 'sync-omie' && btn) {
    event.preventDefault();
    void syncOmie(btn);
  }
});

async function syncOmie(btn: HTMLButtonElement): Promise<void> {
  const labelOriginal = btn.textContent ?? 'Sync Omie';
  btn.disabled = true;
  btn.textContent = 'Sincronizando...';
  try {
    const { catalogo } = await api.admin.omieSyncTrigger();
    const prod = catalogo.produtos;
    const serv = catalogo.servicos;
    if (prod.status === 'ok' || serv.status === 'ok') {
      alert(
        `Catálogo sincronizado!\n\n` +
          `Produtos: ${prod.total} itens (${prod.status})\n` +
          `Serviços: ${serv.total} itens (${serv.status})`,
      );
    } else {
      alert(
        `Sync com problemas:\n\n` +
          `Produtos: ${prod.status}${prod.erro ? ` — ${prod.erro}` : ''}\n` +
          `Serviços: ${serv.status}${serv.erro ? ` — ${serv.erro}` : ''}\n\n` +
          `Tente de novo em alguns minutos (Omie throttle).`,
      );
    }
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : String(err);
    alert(`Falha ao sincronizar: ${msg}`);
  } finally {
    btn.disabled = false;
    btn.textContent = labelOriginal;
  }
}

async function boot(): Promise<void> {
  try {
    await initMsal();
  } catch (err) {
    console.error('[boot] initMsal falhou', err);
  }

  injectLayoutStyles();

  const router = initRouter({
    '/': () => void renderDashboard(),
    '/propostas': () => void renderListaPropostas(),
    '/propostas/nova': () => {
      const tipo = getTipoFromUrl();
      if (!tipo) {
        renderSeletorTipoProposta();
        return;
      }
      if (tipo === 'contrato-recorrente') {
        void renderNovaProposta();
        return;
      }
      if (tipo === 'renovacao-recorrente') {
        // PR-R3: lê ?cnpj e ?contratoOmie pra pré-popular cliente+contrato
        const params = new URLSearchParams(window.location.search);
        const cnpjPreFill = params.get('cnpj') ?? undefined;
        const contratoOmiePreFill = params.get('contratoOmie') ?? undefined;
        void renderNovaProposta(undefined, {
          tipoProposta: 'renovacao-recorrente',
          cnpjPreFill,
          contratoOmiePreFill,
        });
        return;
      }
      renderNovaPropostaGenerica(tipo);
    },
    '/propostas/:id/editar': ({ id }) => void renderNovaProposta(id ?? ''),
    '/propostas/:id': ({ id }) => void renderDetalheProposta(id ?? ''),
    '/admin/precos': () => void renderPrecosAdmin(),
  });

  router.start();
}

void boot();
